<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="isVisible"
        class="resume-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-modal-title"
        @mousedown.self="handleOverlayClick"
      >
        <div class="resume-modal">
          <transition name="content-fade" mode="out-in">
            <!-- Успешная обработка -->

            <div
              v-if="isSuccess"
              key="success"
              class="state-screen success-screen"
            >
              <div class="success-visual">
                <div class="success-circle">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12.5L9.2 17L19 7"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>

                <div class="success-glow"></div>
              </div>

              <h2 class="state-title">
                Резюме импортировано
              </h2>

              <p class="state-description">
                AI обработал документ и добавил информацию в ваш профиль.
                Вы сможете проверить и дополнить её позже.
              </p>

              <div class="result-list">
                <div
                  v-for="item in successItems"
                  :key="item"
                  class="result-item"
                >
                  <span class="result-check">✓</span>
                  <span>{{ item }}</span>
                </div>
              </div>

              <button
                class="primary-button"
                type="button"
                @click="handleSuccessContinue"
              >
                Продолжить регистрацию

                <svg
                  class="button-arrow"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    d="M4 10H16M11 5L16 10L11 15"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <!-- Обработка файла -->

            <div
              v-else-if="isLoading"
              key="loading"
              class="state-screen loading-screen"
            >
              <div class="ai-loader">
                <div class="ai-orb">
                  <span class="ai-orb-core"></span>
                  <span class="ai-orb-ring ai-orb-ring-one"></span>
                  <span class="ai-orb-ring ai-orb-ring-two"></span>
                </div>
              </div>

              <h2 class="state-title">
                AI анализирует резюме
              </h2>

              <p class="state-description">
                Обычно это занимает меньше минуты. Не закрывайте страницу.
              </p>

              <div class="processing-list">
                <div
                  v-for="(item, index) in processingItems"
                  :key="item.label"
                  class="processing-item"
                  :class="getProcessingItemClass(index)"
                >
                  <span class="processing-status">
                    <span
                      v-if="index < activeProcessingIndex"
                      class="processing-check"
                    >
                      ✓
                    </span>

                    <span
                      v-else-if="index === activeProcessingIndex"
                      class="small-spinner"
                    ></span>

                    <span
                      v-else
                      class="processing-dot"
                    ></span>
                  </span>

                  <span>{{ item.label }}</span>
                </div>
              </div>

              <div class="processing-progress">
                <div
                  class="processing-progress-fill"
                  :style="{ width: processingProgress }"
                ></div>
              </div>

              <p class="file-processing-name">
                {{ file?.name }}
              </p>
            </div>

            <!-- Основной экран -->

            <div
              v-else
              key="upload"
              class="upload-content"
            >
              <button
                class="close-button"
                type="button"
                aria-label="Закрыть"
                @click="handleSkip"
              >
                <svg
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    d="M5 5L15 15M15 5L5 15"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>

              <div class="modal-header">
                <div class="ai-badge">
                  <div class="ai-badge-glow"></div>

                  <svg
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path
                      d="M16 3L18.1 11.1L26 13.5L18.1 15.9L16 24L13.9 15.9L6 13.5L13.9 11.1L16 3Z"
                      fill="currentColor"
                    />

                    <path
                      d="M25 20L26.2 24.1L30 25.5L26.2 26.9L25 31L23.8 26.9L20 25.5L23.8 24.1L25 20Z"
                      fill="currentColor"
                      opacity="0.7"
                    />
                  </svg>
                </div>

                <div class="header-copy">
                  <span class="eyebrow">
                    Профиль успешно создан
                  </span>

                  <h2 id="resume-modal-title">
                    Заполнить профиль с помощью AI
                  </h2>
                </div>
              </div>

              <p class="subtitle">
                Загрузите готовое резюме, и AI автоматически перенесёт
                информацию в ваш профиль.
              </p>

              <div class="benefits-grid">
                <div
                  v-for="benefit in benefits"
                  :key="benefit.title"
                  class="benefit-item"
                >
                  <span class="benefit-icon">
                    {{ benefit.icon }}
                  </span>

                  <div>
                    <div class="benefit-title">
                      {{ benefit.title }}
                    </div>

                    <div class="benefit-description">
                      {{ benefit.description }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="upload-zone"
                :class="{
                  active: isDragging,
                  selected: file
                }"
                tabindex="0"
                role="button"
                :aria-label="
                  file
                    ? `Выбран файл ${file.name}`
                    : 'Выбрать файл резюме'
                "
                @click="openFilePicker"
                @keydown.enter.prevent="openFilePicker"
                @keydown.space.prevent="openFilePicker"
                @dragenter.prevent="handleDragEnter"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  hidden
                  @change="handleFileChange"
                />

                <template v-if="!file">
                  <div class="upload-document-icon">
                    <svg
                      viewBox="0 0 32 32"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 3H19L25 9V27C25 28.1 24.1 29 23 29H8C6.9 29 6 28.1 6 27V5C6 3.9 6.9 3 8 3Z"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linejoin="round"
                      />

                      <path
                        d="M19 3V9H25"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linejoin="round"
                      />

                      <path
                        d="M11 17H21M11 21H18"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linecap="round"
                      />
                    </svg>

                    <span class="upload-plus">+</span>
                  </div>

                  <div class="upload-title">
                    Загрузить резюме
                  </div>

                  <div class="upload-description">
                    Перетащите файл сюда или нажмите для выбора
                  </div>

                  <div class="upload-formats">
                    PDF, DOC или DOCX · до {{ maxFileSizeMb }} МБ
                  </div>
                </template>

                <template v-else>
                  <div class="selected-file">
                    <div class="selected-file-icon">
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M7 2H14L19 7V21C19 21.6 18.6 22 18 22H7C6.4 22 6 21.6 6 21V3C6 2.4 6.4 2 7 2Z"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linejoin="round"
                        />

                        <path
                          d="M14 2V7H19"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>

                    <div class="selected-file-copy">
                      <div class="selected-file-name">
                        {{ file.name }}
                      </div>

                      <div class="selected-file-meta">
                        {{ formattedFileSize }}
                        <span class="meta-separator">•</span>
                        {{ selectedFileExtension }}
                      </div>
                    </div>

                    <button
                      class="remove-file-button"
                      type="button"
                      aria-label="Удалить выбранный файл"
                      @click.stop="removeFile"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 5L15 15M15 5L5 15"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <div class="replace-file-text">
                    Нажмите, чтобы выбрать другой файл
                  </div>
                </template>
              </div>

              <transition name="error-fade">
                <div
                  v-if="errorMessage"
                  class="error-message"
                  role="alert"
                >
                  <svg
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="8"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M10 5.8V10.7M10 14H10.01"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>

                  <span>{{ errorMessage }}</span>
                </div>
              </transition>

              <button
                class="primary-button"
                type="button"
                :disabled="!file"
                @click="handleUpload"
              >
                <svg
                  class="button-sparkle"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2L13.6 8.4L20 10L13.6 11.6L12 18L10.4 11.6L4 10L10.4 8.4L12 2Z"
                    fill="currentColor"
                  />

                  <path
                    d="M19 16L19.8 19.2L23 20L19.8 20.8L19 24L18.2 20.8L15 20L18.2 19.2L19 16Z"
                    fill="currentColor"
                    opacity="0.75"
                  />
                </svg>

                Заполнить профиль с помощью AI
              </button>

              <button
                class="skip-button"
                type="button"
                @click="handleSkip"
              >
                Пока пропустить
              </button>

              <p class="privacy-note">
                Документ используется только для заполнения профиля.
              </p>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  ref,
  watch
} from "vue";

import PremiumService from "../api/PremiumService";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },

  maxFileSizeMb: {
    type: Number,
    default: 10
  }
});

const emit = defineEmits([
  "close",
  "skip",
  "complete"
]);

const fileInput = ref(null);
const file = ref(null);

const parsedResumeResult = ref(null);
const isDragging = ref(false);
const isLoading = ref(false);
const isSuccess = ref(false);

const errorMessage = ref("");

const activeProcessingIndex = ref(0);

let processingTimer = null;
let dragDepth = 0;

const allowedExtensions = [
  "pdf",
  "doc",
  "docx"
];

const allowedMimeTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];

const benefits = [
  {
    icon: "💼",
    title: "Опыт работы",
    description: "Компании, должности и обязанности"
  },
  {
    icon: "🎓",
    title: "Образование",
    description: "Учебные заведения и специальности"
  },
  {
    icon: "⚡",
    title: "Навыки",
    description: "Профессиональные компетенции"
  },
  {
    icon: "🌍",
    title: "Языки",
    description: "Языки и уровень владения"
  }
];

const processingItems = [
  {
    label: "Читаем структуру документа"
  },
  {
    label: "Определяем опыт работы"
  },
  {
    label: "Находим образование"
  },
  {
    label: "Выделяем навыки"
  },
  {
    label: "Заполняем профиль"
  }
];

const successItems = [
  "Опыт работы добавлен",
  "Образование добавлено",
  "Навыки определены",
  "Профиль обновлён"
];

const formattedFileSize = computed(() => {
  if (!file.value) return "";

  const bytes = file.value.size;

  if (bytes < 1024) {
    return `${bytes} Б`;
  }

  const kilobytes = bytes / 1024;

  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} КБ`;
  }

  const megabytes = kilobytes / 1024;

  return `${megabytes.toFixed(1)} МБ`;
});

const selectedFileExtension = computed(() => {
  if (!file.value) return "";

  const extension = getFileExtension(file.value.name);

  return extension.toUpperCase();
});

const processingProgress = computed(() => {
  const completedSteps =
    Math.min(
      activeProcessingIndex.value + 1,
      processingItems.length
    );

  const progress =
    (completedSteps / processingItems.length) * 100;

  return `${progress}%`;
});

watch(
  () => props.isVisible,
  (isVisible) => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeydown);
      return;
    }

    document.body.style.overflow = "";
    window.removeEventListener("keydown", handleKeydown);

    resetModal();
  },
  {
    immediate: true
  }
);

function openFilePicker() {
  if (isLoading.value) return;

  fileInput.value?.click();
}

function handleFileChange(event) {
  const selectedFile =
    event.target.files?.[0];

  setFile(selectedFile);
}

function handleDrop(event) {
  dragDepth = 0;
  isDragging.value = false;

  const selectedFile =
    event.dataTransfer?.files?.[0];

  setFile(selectedFile);
}

function handleDragEnter() {
  dragDepth += 1;
  isDragging.value = true;
}

function handleDragOver() {
  isDragging.value = true;
}

function handleDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);

  if (dragDepth === 0) {
    isDragging.value = false;
  }
}

function setFile(selectedFile) {
  errorMessage.value = "";

  if (!selectedFile) return;

  const validationError =
    validateFile(selectedFile);

  if (validationError) {
    errorMessage.value = validationError;
    resetFileInput();
    return;
  }

  file.value = selectedFile;
}

function validateFile(selectedFile) {
  const extension =
    getFileExtension(selectedFile.name);

  const hasAllowedExtension =
    allowedExtensions.includes(extension);

  const hasAllowedMimeType =
    !selectedFile.type ||
    allowedMimeTypes.includes(selectedFile.type);

  if (
    !hasAllowedExtension ||
    !hasAllowedMimeType
  ) {
    return "Поддерживаются только файлы PDF, DOC и DOCX";
  }

  const maximumFileSize =
    props.maxFileSizeMb * 1024 * 1024;

  if (selectedFile.size > maximumFileSize) {
    return `Размер файла не должен превышать ${props.maxFileSizeMb} МБ`;
  }

  if (selectedFile.size === 0) {
    return "Выбранный файл пуст";
  }

  return "";
}

function getFileExtension(fileName) {
  return String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();
}

function removeFile() {
  if (isLoading.value) return;

  file.value = null;
  errorMessage.value = "";

  resetFileInput();
}

function resetFileInput() {
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

async function handleUpload() {
  if (!file.value || isLoading.value) return;

  errorMessage.value = "";
  isLoading.value = true;
  activeProcessingIndex.value = 0;

  startProcessingAnimation();

  try {
    const result = await PremiumService.aiResumeParse(file.value);

    stopProcessingAnimation();

    activeProcessingIndex.value = processingItems.length;

    await wait(350);

    parsedResumeResult.value = result;
    isSuccess.value = true;
  } catch (error) {
    console.error(
      "❌ Ошибка обработки резюме:",
      error
    );

    stopProcessingAnimation();
    isLoading.value = false;

    errorMessage.value =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Не удалось обработать резюме. Попробуйте другой файл";
  }
}

function startProcessingAnimation() {
  stopProcessingAnimation();

  processingTimer = window.setInterval(() => {
    if (
      activeProcessingIndex.value <
      processingItems.length - 1
    ) {
      activeProcessingIndex.value += 1;
    }
  }, 900);
}

function stopProcessingAnimation() {
  if (!processingTimer) return;

  window.clearInterval(processingTimer);
  processingTimer = null;
}

function getProcessingItemClass(index) {
  return {
    completed:
      index < activeProcessingIndex.value,

    active:
      index === activeProcessingIndex.value,

    pending:
      index > activeProcessingIndex.value
  };
}

function handleSuccessContinue() {
  emit("complete", parsedResumeResult.value);
}

function handleSkip() {
  if (isLoading.value) return;

  emit("skip");
  emit("close");
}

function handleOverlayClick() {
  handleSkip();
}

function handleKeydown(event) {
  if (
    event.key !== "Escape" ||
    isLoading.value
  ) {
    return;
  }

  handleSkip();
}

function resetModal() {
  stopProcessingAnimation();

  file.value = null;
  parsedResumeResult.value = null;

  isDragging.value = false;
  isLoading.value = false;
  isSuccess.value = false;

  errorMessage.value = "";
  activeProcessingIndex.value = 0;

  dragDepth = 0;

  resetFileInput();
}

function wait(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

onBeforeUnmount(() => {
  stopProcessingAnimation();

  document.body.style.overflow = "";

  window.removeEventListener(
    "keydown",
    handleKeydown
  );
});
</script>

<style scoped>
.resume-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 300000;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  padding: 24px;

  background:
    radial-gradient(
      circle at 50% 15%,
      rgba(40, 135, 247, 0.18),
      transparent 38%
    ),
    rgba(8, 11, 17, 0.72);

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.resume-modal {
  position: relative;

  width: min(560px, 100%);
  max-height: calc(100vh - 48px);

  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;

  padding: 32px;

  border:
    1px solid
    var(--registration-resume-border);

  border-radius: 28px;

  background:
    linear-gradient(
      145deg,
      var(--registration-resume-bg-top) 0%,
      var(--registration-resume-bg-bottom) 100%
    );

  box-shadow:
    0 30px 90px rgba(0, 0, 0, 0.32),
    inset 0 1px 0
    var(--registration-resume-highlight);

  color:
    var(--registration-resume-text);
}

.upload-content {
  position: relative;
}

.close-button {
  position: absolute;
  top: -12px;
  right: -12px;
  z-index: 5;

  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border:
    1px solid
    var(--registration-resume-soft-border);

  border-radius: 50%;

  background:
    var(--registration-resume-soft-bg);

  color:
    var(--registration-resume-muted);

  cursor: pointer;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.close-button svg {
  width: 19px;
  height: 19px;
}

.close-button:hover {
  color:
    var(--registration-resume-text);

  background:
    var(--registration-resume-soft-bg-hover);

  transform: rotate(4deg);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;

  padding-right: 34px;
}

.ai-badge {
  position: relative;

  width: 58px;
  height: 58px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border:
    1px solid
    rgba(109, 174, 255, 0.35);

  border-radius: 18px;

  background:
    linear-gradient(
      145deg,
      #176edc 0%,
      #3996ff 55%,
      #72b6ff 100%
    );

  color: #ffffff;

  box-shadow:
    0 14px 32px
    rgba(40, 135, 247, 0.28),
    inset 0 1px 0
    rgba(255, 255, 255, 0.35);
}

.ai-badge svg {
  position: relative;
  z-index: 2;

  width: 32px;
  height: 32px;
}

.ai-badge-glow {
  position: absolute;

  top: -25px;
  right: -20px;

  width: 70px;
  height: 70px;

  border-radius: 50%;

  background:
    rgba(255, 255, 255, 0.34);

  filter: blur(18px);
}

.header-copy {
  min-width: 0;
}

.eyebrow {
  display: block;

  margin-bottom: 5px;

  color: #2887f7;

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 11px;
  line-height: 1.2;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.modal-header h2 {
  margin: 0;

  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Bold",
    sans-serif;
  font-size: 22px;
  line-height: 1.2;
}

.subtitle {
  max-width: 490px;

  margin: 18px 0 0;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 14px;
  line-height: 1.55;
}

.benefits-grid {
  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 10px;

  margin-top: 22px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  min-width: 0;
  padding: 12px;

  border:
    1px solid
    var(--registration-resume-soft-border);

  border-radius: 14px;

  background:
    var(--registration-resume-soft-bg);
}

.benefit-icon {
  width: 30px;
  height: 30px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;

  background:
    rgba(40, 135, 247, 0.1);

  font-size: 15px;
}

.benefit-title {
  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 12px;
  line-height: 1.25;
}

.benefit-description {
  margin-top: 3px;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 10px;
  line-height: 1.35;
}

.upload-zone {
  min-height: 182px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  margin-top: 20px;
  padding: 24px;

  border:
    2px dashed
    var(--registration-resume-upload-border);

  border-radius: 20px;

  background:
    var(--registration-resume-upload-bg);

  text-align: center;

  cursor: pointer;
  outline: none;

  transition:
    border-color 0.22s ease,
    background-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.upload-zone:hover,
.upload-zone:focus-visible,
.upload-zone.active {
  border-color:
    rgba(40, 135, 247, 0.78);

  background:
    rgba(40, 135, 247, 0.07);

  box-shadow:
    0 0 0 5px
    rgba(40, 135, 247, 0.08);

  transform:
    translateY(-1px);
}

.upload-zone.selected {
  min-height: 148px;

  border-style: solid;
  border-color:
    rgba(40, 135, 247, 0.42);

  background:
    rgba(40, 135, 247, 0.06);
}

.upload-document-icon {
  position: relative;

  width: 54px;
  height: 54px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 17px;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.14),
      rgba(40, 135, 247, 0.05)
    );

  color: #2887f7;
}

.upload-document-icon svg {
  width: 30px;
  height: 30px;
}

.upload-plus {
  position: absolute;
  right: -4px;
  bottom: -4px;

  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  border:
    2px solid
    var(--registration-resume-bg-bottom);

  border-radius: 50%;

  background: #2887f7;
  color: #ffffff;

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 14px;
}

.upload-title {
  margin-top: 13px;

  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 15px;
}

.upload-description {
  margin-top: 5px;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.upload-formats {
  margin-top: 9px;

  color:
    var(--registration-resume-subtle);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 10px;
  line-height: 1.4;
}

.selected-file {
  width: 100%;

  display: flex;
  align-items: center;
  gap: 12px;

  box-sizing: border-box;
  padding: 14px;

  border:
    1px solid
    rgba(40, 135, 247, 0.22);

  border-radius: 15px;

  background:
    var(--registration-resume-file-bg);

  text-align: left;
}

.selected-file-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;

  background:
    rgba(40, 135, 247, 0.12);

  color: #2887f7;
}

.selected-file-icon svg {
  width: 23px;
  height: 23px;
}

.selected-file-copy {
  min-width: 0;
  flex: 1;
}

.selected-file-name {
  overflow: hidden;

  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 13px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.selected-file-meta {
  margin-top: 4px;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 11px;
}

.meta-separator {
  margin: 0 4px;
}

.remove-file-button {
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 50%;

  background:
    var(--registration-resume-soft-bg);

  color:
    var(--registration-resume-muted);

  cursor: pointer;

  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

.remove-file-button svg {
  width: 17px;
  height: 17px;
}

.remove-file-button:hover {
  background:
    rgba(224, 66, 66, 0.11);

  color: #db4848;
}

.replace-file-text {
  margin-top: 11px;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 10px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 9px;

  margin-top: 12px;
  padding: 11px 13px;

  border:
    1px solid
    rgba(222, 66, 66, 0.24);

  border-radius: 12px;

  background:
    rgba(222, 66, 66, 0.08);

  color: #df4d4d;

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 11px;
  line-height: 1.4;
}

.error-message svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.primary-button {
  width: 100%;
  min-height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  box-sizing: border-box;

  margin-top: 20px;
  padding: 13px 20px;

  border: none;
  border-radius: 15px;

  background:
    linear-gradient(
      135deg,
      #1974e8 0%,
      #2887f7 48%,
      #59a8ff 100%
    );

  color: #ffffff;

  box-shadow:
    0 12px 28px
    rgba(40, 135, 247, 0.24),
    inset 0 1px 0
    rgba(255, 255, 255, 0.25);

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 14px;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.primary-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 16px 32px
    rgba(40, 135, 247, 0.32),
    inset 0 1px 0
    rgba(255, 255, 255, 0.28);
}

.primary-button:active:not(:disabled) {
  transform:
    translateY(0);
}

.primary-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.button-sparkle,
.button-arrow {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.skip-button {
  width: 100%;

  margin-top: 10px;
  padding: 10px;

  border: none;

  background: transparent;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 12px;

  cursor: pointer;

  transition:
    color 0.2s ease;
}

.skip-button:hover {
  color:
    var(--registration-resume-text);
}

.privacy-note {
  margin: 4px 0 0;

  color:
    var(--registration-resume-subtle);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 9px;
  line-height: 1.4;
  text-align: center;
}

/* Processing */

.state-screen {
  min-height: 530px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  padding: 12px 20px;

  text-align: center;
}

.ai-loader {
  height: 116px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-orb {
  position: relative;

  width: 76px;
  height: 76px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-orb-core {
  position: relative;
  z-index: 3;

  width: 50px;
  height: 50px;

  border-radius: 50%;

  background:
    radial-gradient(
      circle at 35% 28%,
      #d7edff 0%,
      #74baff 20%,
      #2887f7 52%,
      #135fc2 100%
    );

  box-shadow:
    0 0 32px
    rgba(40, 135, 247, 0.5),
    inset -8px -10px 18px
    rgba(0, 36, 96, 0.24),
    inset 6px 7px 14px
    rgba(255, 255, 255, 0.32);

  animation:
    orbPulse 1.8s ease-in-out infinite;
}

.ai-orb-ring {
  position: absolute;

  border:
    1px solid
    rgba(72, 157, 255, 0.42);

  border-radius: 50%;
}

.ai-orb-ring-one {
  inset: 6px;

  animation:
    orbSpin 4s linear infinite;
}

.ai-orb-ring-two {
  inset: -8px;

  border-style: dashed;

  opacity: 0.65;

  animation:
    orbSpinReverse 6s linear infinite;
}

.state-title {
  margin: 12px 0 0;

  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Bold",
    sans-serif;
  font-size: 22px;
  line-height: 1.25;
}

.state-description {
  max-width: 400px;

  margin: 10px 0 0;

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 13px;
  line-height: 1.55;
}

.processing-list {
  width: min(370px, 100%);

  display: flex;
  flex-direction: column;
  gap: 7px;

  margin-top: 28px;
}

.processing-item {
  display: flex;
  align-items: center;
  gap: 11px;

  padding: 11px 13px;

  border:
    1px solid
    var(--registration-resume-soft-border);

  border-radius: 12px;

  background:
    var(--registration-resume-soft-bg);

  color:
    var(--registration-resume-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 11px;
  text-align: left;

  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    background-color 0.2s ease,
    opacity 0.2s ease;
}

.processing-item.completed {
  color:
    var(--registration-resume-text);
}

.processing-item.active {
  border-color:
    rgba(40, 135, 247, 0.45);

  background:
    rgba(40, 135, 247, 0.08);

  color:
    var(--registration-resume-text);
}

.processing-item.pending {
  opacity: 0.5;
}

.processing-status {
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;
}

.processing-check {
  width: 18px;
  height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    rgba(40, 135, 247, 0.12);

  color: #2887f7;

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 10px;
}

.small-spinner {
  width: 14px;
  height: 14px;

  display: inline-block;

  border:
    2px solid
    rgba(40, 135, 247, 0.22);

  border-top-color: #2887f7;
  border-radius: 50%;

  animation:
    spin 0.7s linear infinite;
}

.processing-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background:
    var(--registration-resume-subtle);
}

.processing-progress {
  width: min(370px, 100%);
  height: 5px;

  overflow: hidden;

  margin-top: 18px;

  border-radius: 999px;

  background:
    var(--registration-resume-soft-bg);
}

.processing-progress-fill {
  height: 100%;

  border-radius: inherit;

  background:
    linear-gradient(
      90deg,
      #1974e8,
      #55a6ff
    );

  transition:
    width 0.45s ease;
}

.file-processing-name {
  max-width: 370px;

  overflow: hidden;

  margin: 12px 0 0;

  color:
    var(--registration-resume-subtle);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 10px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Success */

.success-visual {
  position: relative;

  width: 96px;
  height: 96px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.success-circle {
  position: relative;
  z-index: 2;

  width: 72px;
  height: 72px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    linear-gradient(
      145deg,
      #1974e8,
      #4ca2ff
    );

  color: #ffffff;

  box-shadow:
    0 18px 42px
    rgba(40, 135, 247, 0.32),
    inset 0 1px 0
    rgba(255, 255, 255, 0.32);

  animation:
    successPop 0.38s ease;
}

.success-circle svg {
  width: 35px;
  height: 35px;
}

.success-glow {
  position: absolute;

  width: 90px;
  height: 90px;

  border-radius: 50%;

  background:
    rgba(40, 135, 247, 0.24);

  filter: blur(24px);
}

.result-list {
  width: min(340px, 100%);

  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 8px;

  margin-top: 24px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 10px 11px;

  border:
    1px solid
    var(--registration-resume-soft-border);

  border-radius: 11px;

  background:
    var(--registration-resume-soft-bg);

  color:
    var(--registration-resume-text);

  font-family:
    "Eastman-Regular",
    sans-serif;
  font-size: 10px;
  text-align: left;
}

.result-check {
  width: 17px;
  height: 17px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    rgba(40, 135, 247, 0.12);

  color: #2887f7;

  font-family:
    "Eastman-Medium",
    sans-serif;
  font-size: 9px;
}

.success-screen .primary-button {
  max-width: 350px;

  margin-top: 26px;
}

/* Animations */

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition:
    opacity 0.24s ease;
}

.modal-fade-enter-active .resume-modal,
.modal-fade-leave-active .resume-modal {
  transition:
    opacity 0.24s ease,
    transform 0.24s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .resume-modal,
.modal-fade-leave-to .resume-modal {
  opacity: 0;
  transform:
    translateY(12px)
    scale(0.97);
}

.content-fade-enter-active,
.content-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.content-fade-enter-from {
  opacity: 0;
  transform:
    translateY(7px);
}

.content-fade-leave-to {
  opacity: 0;
  transform:
    translateY(-5px);
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform:
    translateY(-4px);
}

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

@keyframes orbPulse {
  0%,
  100% {
    transform:
      scale(0.96);
  }

  50% {
    transform:
      scale(1.04);
  }
}

@keyframes orbSpin {
  to {
    transform:
      rotate(360deg);
  }
}

@keyframes orbSpinReverse {
  to {
    transform:
      rotate(-360deg);
  }
}

@keyframes successPop {
  from {
    opacity: 0;
    transform:
      scale(0.76);
  }

  to {
    opacity: 1;
    transform:
      scale(1);
  }
}

@media (max-width: 620px) {
  .resume-modal-overlay {
    align-items: flex-end;

    padding: 0;
  }

  .resume-modal {
    width: 100%;
    max-height: 94vh;

    padding:
      25px 18px
      calc(
        22px +
        env(safe-area-inset-bottom)
      );

    border-right: none;
    border-bottom: none;
    border-left: none;

    border-radius:
      28px 28px 0 0;
  }

  .close-button {
    top: -7px;
    right: -2px;
  }

  .modal-header {
    gap: 13px;
  }

  .ai-badge {
    width: 50px;
    height: 50px;

    border-radius: 16px;
  }

  .ai-badge svg {
    width: 28px;
    height: 28px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .eyebrow {
    font-size: 9px;
  }

  .subtitle {
    margin-top: 15px;

    font-size: 12px;
  }

  .benefits-grid {
    gap: 7px;

    margin-top: 17px;
  }

  .benefit-item {
    gap: 8px;

    padding: 10px;
  }

  .benefit-icon {
    width: 27px;
    height: 27px;

    font-size: 13px;
  }

  .benefit-title {
    font-size: 11px;
  }

  .benefit-description {
    display: none;
  }

  .upload-zone {
    min-height: 164px;

    margin-top: 16px;
    padding: 20px 14px;
  }

  .upload-zone.selected {
    min-height: 134px;
  }

  .primary-button {
    min-height: 49px;

    margin-top: 16px;

    border-radius: 999px;

    font-size: 12px;
  }

  .skip-button {
    font-size: 11px;
  }

  .state-screen {
    min-height: 510px;

    padding-right: 2px;
    padding-left: 2px;
  }

  .state-title {
    font-size: 19px;
  }

  .state-description {
    font-size: 12px;
  }

  .result-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 390px) {
  .resume-modal {
    padding-right: 15px;
    padding-left: 15px;
  }

  .benefits-grid {
    grid-template-columns: 1fr 1fr;
  }

  .selected-file {
    padding: 11px;
  }

  .selected-file-name {
    max-width: 185px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .resume-modal,
  .upload-zone,
  .primary-button,
  .close-button,
  .ai-orb-core,
  .ai-orb-ring,
  .success-circle {
    animation-duration:
      0.01ms !important;

    transition-duration:
      0.01ms !important;
  }
}
</style>

<style>
:root {
  --registration-resume-bg-top:
    rgba(43, 43, 46, 0.98);

  --registration-resume-bg-bottom:
    rgba(32, 33, 36, 0.98);

  --registration-resume-text:
    #ffffff;

  --registration-resume-muted:
    rgba(255, 255, 255, 0.64);

  --registration-resume-subtle:
    rgba(255, 255, 255, 0.38);

  --registration-resume-border:
    rgba(255, 255, 255, 0.09);

  --registration-resume-soft-border:
    rgba(255, 255, 255, 0.07);

  --registration-resume-highlight:
    rgba(255, 255, 255, 0.08);

  --registration-resume-soft-bg:
    rgba(255, 255, 255, 0.045);

  --registration-resume-soft-bg-hover:
    rgba(255, 255, 255, 0.09);

  --registration-resume-upload-border:
    rgba(255, 255, 255, 0.14);

  --registration-resume-upload-bg:
    rgba(255, 255, 255, 0.025);

  --registration-resume-file-bg:
    rgba(19, 20, 23, 0.42);
}

[data-theme="light"] {
  --registration-resume-bg-top:
    rgba(255, 255, 255, 0.98);

  --registration-resume-bg-bottom:
    rgba(248, 250, 253, 0.98);

  --registration-resume-text:
    #191b20;

  --registration-resume-muted:
    rgba(25, 27, 32, 0.62);

  --registration-resume-subtle:
    rgba(25, 27, 32, 0.4);

  --registration-resume-border:
    rgba(10, 24, 48, 0.1);

  --registration-resume-soft-border:
    rgba(10, 24, 48, 0.07);

  --registration-resume-highlight:
    rgba(255, 255, 255, 0.9);

  --registration-resume-soft-bg:
    rgba(17, 39, 72, 0.035);

  --registration-resume-soft-bg-hover:
    rgba(17, 39, 72, 0.07);

  --registration-resume-upload-border:
    rgba(22, 45, 77, 0.17);

  --registration-resume-upload-bg:
    rgba(17, 39, 72, 0.02);

  --registration-resume-file-bg:
    rgba(255, 255, 255, 0.72);
}
</style>